import { useEffect, useRef } from 'react';
import { useAccount, useDisconnect } from 'wagmi';
import { useUserAuth } from '@/providers/UserAuthProvider';

/**
 * Hook that syncs wallet connection state with authentication state
 * - Disconnects wallet when signing out (if signed in with wallet)
 * - Signs out when wallet is disconnected (if signed in with wallet)
 */
export function useWalletAuthSync() {
  const { isConnected, address } = useAccount();
  const { user, logout } = useUserAuth();
  const { disconnect } = useDisconnect();
  const previousConnectionRef = useRef<{ isConnected: boolean; address?: string }>({
    isConnected: false,
    address: undefined,
  });

  useEffect(() => {
    const wasConnected = previousConnectionRef.current.isConnected;
    const previousAddress = previousConnectionRef.current.address;

    // Check if user is signed in with wallet (uid starts with 'evm:')
    const isWalletUser = user?.uid?.startsWith('evm:');

    // Case 1: Wallet was disconnected
    if (wasConnected && !isConnected && isWalletUser) {
      console.log('Wallet disconnected, signing out wallet user');
      logout().catch(err => console.error('Failed to logout on wallet disconnect:', err));
    }

    // Case 2: Wallet address changed (user switched accounts)
    if (isConnected && wasConnected && address && previousAddress && address !== previousAddress && isWalletUser) {
      console.log('Wallet address changed, signing out previous wallet user');
      logout().catch(err => console.error('Failed to logout on wallet address change:', err));
    }

    // Update previous state
    previousConnectionRef.current = {
      isConnected,
      address,
    };
  }, [isConnected, address, user, logout]);

  // Expose disconnect function for logout to use
  return { disconnect };
}
